import { gql, useQuery, useMutation } from '@apollo/client';
import CCEvents, {
  EventNames,
  EventPropertyKeys,
} from '@companycam/companycam-events';
import { get } from 'lodash';
import toast from 'helpers/toast';
import { BILLING_SUBSCRIPTION_PLAN_UPDATE_MUTATION } from '../../gql/BillingSubscriptionPlanUpdateMutation.graphql.js';
import COMPANY_BILLING_STATUS_QUERY from 'components/billing/gql/CompanyBillingStatusQuery.graphql.js';
import COMPANY_HAS_PAYMENT_SOURCE_QUERY from 'components/billing/gql/HasPaymentSourceQuery.graphql';

import { getSubscriptionOptionsFromPlan } from './helpers';

const logEvents = (data) => {
  const startedTrial = get(
    data,
    'billingSubscriptionPlanUpdate.startedTrial',
    null,
  );
  const updatedStatus = get(
    data,
    'billingSubscriptionPlanUpdate.updatedStatus',
    null,
  );
  if (startedTrial) {
    CCEvents.logEvent(EventNames.START_TRIAL);
  }
  if (updatedStatus) {
    const { action, planTierFrom, planTierTo, cycleFrom, cycleTo } =
      updatedStatus;
    if (action in EventNames) {
      CCEvents.logEvent(EventNames[action], {
        [EventPropertyKeys.PLAN_TIER_FROM]: planTierFrom,
        [EventPropertyKeys.PLAN_TIER_TO]: planTierTo,
        [EventPropertyKeys.PLAN_CYCLE_FROM]: cycleFrom,
        [EventPropertyKeys.PLAN_CYCLE_TO]: cycleTo,
      });
    }
  }
};

const COMPANY_BILLING_QUERY = gql`
  query CompanyBillingSubscriptionInfo {
    viewer {
      id

      company {
        id
        usersCount
      }

      billing {
        availablePlans {
          id
          identifier
          subscriptionOptions {
            edges {
              node {
                id
                cycle
                title
                price
                priceInCents
                amountSavedInCents
              }
            }
          }
        }

        subscription {
          id
          subscriptionEnd
          paymentSourceOnFile
        }
      }
    }
  }
`;

const SUCCESS_MESSAGE = '🎉 Plan Updated!';
const ERROR_MESSAGE = 'There was a problem updating plan';

const NOOP = () => {};
const COMPANY_BILLING_DETAILED_QUERY = 'CompanyBillingDetailed';

export default function useSubscriptionPlanUpdate({
  onComplete = NOOP,
  customToast = false,
  showToast = true,
} = {}) {
  const { data } = useQuery(COMPANY_BILLING_QUERY);
  const subscription = get(data, 'viewer.billing.subscription', {});

  const [billingSubscriptionPlanUpdate, { loading }] = useMutation(
    BILLING_SUBSCRIPTION_PLAN_UPDATE_MUTATION,
    {
      refetchQueries: [
        COMPANY_BILLING_DETAILED_QUERY,
        COMPANY_BILLING_STATUS_QUERY,
        COMPANY_HAS_PAYMENT_SOURCE_QUERY,
      ],
      awaitRefetchQueries: true,
      onCompleted: (newData) => {
        logEvents(newData);
        const updateErrors = get(
          newData,
          'billingSubscriptionPlanUpdate.errors',
          [],
        );
        if (updateErrors.length) {
          toast.error(ERROR_MESSAGE);
        } else {
          if (showToast) {
            if (customToast) {
              toast.show(customToast);
            } else {
              toast.success(SUCCESS_MESSAGE);
            }
          }
          onComplete();
        }
      },
      onError: () => {
        toast.error(ERROR_MESSAGE);
      },
    },
  );

  const subscriptionPlanUpdate = async (subscriptionOption) => {
    await billingSubscriptionPlanUpdate({
      variables: {
        input: {
          id: subscription.id,
          subscriptionOptionId: subscriptionOption.id,
        },
      },
    });
  };

  const getDefaultSubscriptionOption = (currentPlan) => {
    const availablePlans = get(data, 'viewer.billing.availablePlans', []);
    const companyPlanFromAvailablePlans =
      availablePlans.find(
        (availablePlan) => availablePlan.id === currentPlan.id,
      ) || {};
    const subscriptionOptions = getSubscriptionOptionsFromPlan(
      companyPlanFromAvailablePlans,
    );

    return subscriptionOptions[0] || {};
  };

  return {
    loading,
    subscriptionPlanUpdate,
    getDefaultSubscriptionOption,
    subscription,
  };
}
